import React, { useEffect, useState, Component } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminHeader from '../../components/admin/AdminHeader';
import { HelpCircleIcon, BookOpenIcon, SearchIcon, PlayCircleIcon, FileTextIcon, MessageCircleIcon, PhoneIcon, MailIcon, ExternalLinkIcon, ChevronRightIcon, ChevronDownIcon, CheckCircleIcon, UserIcon, FileIcon, BuildingIcon, LayoutIcon, CreditCardIcon, BarChart2Icon, BellIcon, SettingsIcon, LockIcon, AlertCircleIcon, InfoIcon, RefreshCwIcon, ThumbsUpIcon, ThumbsDownIcon, ClipboardIcon, DownloadIcon, ArrowRightIcon, PlusIcon, XIcon } from 'lucide-react';
// Help Topic Type
interface HelpTopic {
  id: string;
  title: string;
  category: string;
  content: string;
  forSuperAdmin: boolean;
  forMdaAdmin: boolean;
  icon: React.ReactNode;
  videoUrl?: string;
}
// Help Category Type
interface HelpCategory {
  id: string;
  name: string;
  icon: React.ReactNode;
  description: string;
  forSuperAdmin: boolean;
  forMdaAdmin: boolean;
}
// FAQ Type
interface FAQ {
  id: string;
  question: string;
  answer: string;
  forSuperAdmin: boolean;
  forMdaAdmin: boolean;
}
// Video Tutorial Type
interface VideoTutorial {
  id: string;
  title: string;
  thumbnail: string;
  duration: string;
  category: string;
}
// Video Tutorials Data
const videoTutorials: VideoTutorial[] = [{
  id: 'video1',
  title: 'Getting Started with the Admin Dashboard',
  thumbnail: 'https://img.youtube.com/vi/dQw4w9WgXcQ/hqdefault.jpg',
  duration: '5:32',
  category: 'Basics'
}, {
  id: 'video2',
  title: 'Managing MDAs and Services',
  thumbnail: 'https://img.youtube.com/vi/dQw4w9WgXcQ/hqdefault.jpg',
  duration: '8:15',
  category: 'MDAs'
}, {
  id: 'video3',
  title: 'Processing User Applications',
  thumbnail: 'https://img.youtube.com/vi/dQw4w9WgXcQ/hqdefault.jpg',
  duration: '7:48',
  category: 'Applications'
}, {
  id: 'video4',
  title: 'Generating and Understanding Reports',
  thumbnail: 'https://img.youtube.com/vi/dQw4w9WgXcQ/hqdefault.jpg',
  duration: '10:22',
  category: 'Reports'
}, {
  id: 'video5',
  title: 'User Management and Permissions',
  thumbnail: 'https://img.youtube.com/vi/dQw4w9WgXcQ/hqdefault.jpg',
  duration: '6:45',
  category: 'Users'
}, {
  id: 'video6',
  title: 'Payment Processing and Reconciliation',
  thumbnail: 'https://img.youtube.com/vi/dQw4w9WgXcQ/hqdefault.jpg',
  duration: '9:10',
  category: 'Payments'
}];
// Document Type
interface Document {
  id: string;
  title: string;
  description: string;
  fileSize: string;
  fileType: string;
  category: string;
}
// Documents Data
const documents: Document[] = [{
  id: 'doc1',
  title: 'Admin User Manual',
  description: 'Complete guide to using the admin dashboard',
  fileSize: '2.5 MB',
  fileType: 'PDF',
  category: 'Manuals'
}, {
  id: 'doc2',
  title: 'MDA Management Guide',
  description: 'Detailed instructions for managing MDAs',
  fileSize: '1.8 MB',
  fileType: 'PDF',
  category: 'Manuals'
}, {
  id: 'doc3',
  title: 'Service Configuration Handbook',
  description: 'How to set up and configure services',
  fileSize: '3.2 MB',
  fileType: 'PDF',
  category: 'Manuals'
}, {
  id: 'doc4',
  title: 'Application Processing Procedures',
  description: 'Standard operating procedures for processing applications',
  fileSize: '1.5 MB',
  fileType: 'PDF',
  category: 'Procedures'
}, {
  id: 'doc5',
  title: 'Payment System Integration Guide',
  description: 'Technical guide for payment system integrations',
  fileSize: '4.1 MB',
  fileType: 'PDF',
  category: 'Technical'
}, {
  id: 'doc6',
  title: 'Report Generation Templates',
  description: 'Templates for standard reports',
  fileSize: '1.2 MB',
  fileType: 'ZIP',
  category: 'Templates'
}];
const Help: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [activeTopicId, setActiveTopicId] = useState<string | null>(null);
  const [activeFaqId, setActiveFaqId] = useState<string | null>(null);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [helpTopics, setHelpTopics] = useState<HelpTopic[]>([]);
  const [helpCategories, setHelpCategories] = useState<HelpCategory[]>([]);
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [filteredTopics, setFilteredTopics] = useState<HelpTopic[]>([]);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [currentVideo, setCurrentVideo] = useState<string | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackType, setFeedbackType] = useState<'positive' | 'negative' | null>(null);
  const {
    hasPermission,
    isSuperAdmin,
    getMdaName
  } = useAdminAuth();
  // Set initial sidebar state based on screen size
  useEffect(() => {
    const handleResize = () => {
      setIsSidebarOpen(window.innerWidth >= 1024);
    };
    // Set initial state
    handleResize();
    // Add event listener
    window.addEventListener('resize', handleResize);
    // Simulate loading data
    setTimeout(() => {
      // Define help categories
      const categories: HelpCategory[] = [{
        id: 'getting-started',
        name: 'Getting Started',
        icon: <BookOpenIcon size={20} className="text-green-600" data-id="element-1675" />,
        description: 'Learn the basics of using the admin portal',
        forSuperAdmin: true,
        forMdaAdmin: true
      }, {
        id: 'user-management',
        name: 'User Management',
        icon: <UserIcon size={20} className="text-blue-600" data-id="element-1676" />,
        description: 'Managing users and permissions',
        forSuperAdmin: true,
        forMdaAdmin: true
      }, {
        id: 'mda-management',
        name: 'MDA Management',
        icon: <BuildingIcon size={20} className="text-purple-600" data-id="element-1677" />,
        description: 'Managing Ministries, Departments, and Agencies',
        forSuperAdmin: true,
        forMdaAdmin: false
      }, {
        id: 'applications',
        name: 'Applications',
        icon: <FileIcon size={20} className="text-orange-600" data-id="element-1678" />,
        description: 'Managing service applications',
        forSuperAdmin: true,
        forMdaAdmin: true
      }, {
        id: 'services',
        name: 'Services',
        icon: <LayoutIcon size={20} className="text-indigo-600" data-id="element-1679" />,
        description: 'Managing available services',
        forSuperAdmin: true,
        forMdaAdmin: true
      }, {
        id: 'payments',
        name: 'Payments',
        icon: <CreditCardIcon size={20} className="text-green-600" data-id="element-1680" />,
        description: 'Payment processing and management',
        forSuperAdmin: true,
        forMdaAdmin: true
      }, {
        id: 'reports',
        name: 'Reports & Analytics',
        icon: <BarChart2Icon size={20} className="text-blue-600" data-id="element-1681" />,
        description: 'Generating and interpreting reports',
        forSuperAdmin: true,
        forMdaAdmin: true
      }, {
        id: 'notifications',
        name: 'Notifications',
        icon: <BellIcon size={20} className="text-yellow-600" data-id="element-1682" />,
        description: 'Managing system notifications',
        forSuperAdmin: true,
        forMdaAdmin: true
      }, {
        id: 'settings',
        name: 'Settings',
        icon: <SettingsIcon size={20} className="text-gray-600" data-id="element-1683" />,
        description: 'System configuration and settings',
        forSuperAdmin: true,
        forMdaAdmin: true
      }, {
        id: 'security',
        name: 'Security',
        icon: <LockIcon size={20} className="text-red-600" data-id="element-1684" />,
        description: 'Security best practices and settings',
        forSuperAdmin: true,
        forMdaAdmin: true
      }];
      // Define help topics
      const topics: HelpTopic[] = [
      // Getting Started Topics
      {
        id: 'gs-overview',
        title: 'Admin Portal Overview',
        category: 'getting-started',
        content: `
            <h2>Welcome to the Admin Portal</h2>
            <p>The Admin Portal is your central hub for managing all aspects of the government service platform. This overview will help you understand the key components and how to navigate the system effectively.</p>
            <h3>Key Components</h3>
            <ul>
              <li><strong>Dashboard:</strong> Your central hub showing key metrics and recent activities</li>
              <li><strong>User Management:</strong> Manage citizen accounts and admin users</li>
              ${isSuperAdmin() ? '<li><strong>MDA Management:</strong> Add and configure government ministries, departments, and agencies</li>' : ''}
              <li><strong>Applications:</strong> Process and manage service applications from citizens</li>
              <li><strong>Services:</strong> Configure and manage available government services</li>
              <li><strong>Payments:</strong> Track and manage payment transactions</li>
              <li><strong>Reports:</strong> Generate insights and analytics about platform usage</li>
              <li><strong>Settings:</strong> Configure system-wide or MDA-specific settings</li>
            </ul>
            <h3>Navigation</h3>
            <p>The main navigation menu is located on the left side of the screen. You can collapse it using the menu button in the top left corner for more screen space.</p>
            <h3>Getting Help</h3>
            <p>If you need assistance, you can:</p>
            <ul>
              <li>Use the search function in the Help Center</li>
              <li>Browse help topics by category</li>
              <li>Contact support through the "Contact Support" section</li>
            </ul>
          `,
        forSuperAdmin: true,
        forMdaAdmin: true,
        icon: <InfoIcon size={20} className="text-blue-600" data-id="element-1685" />,
        videoUrl: 'https://www.example.com/videos/admin-portal-overview'
      }, {
        id: 'gs-first-steps',
        title: 'First Steps for New Administrators',
        category: 'getting-started',
        content: `
            <h2>Getting Started as a New Administrator</h2>
            <p>Welcome to your new role! This guide will help you get started with the essential tasks and settings to set up your administrator account.</p>
            <h3>1. Complete Your Profile</h3>
            <p>Start by completing your administrator profile:</p>
            <ul>
              <li>Navigate to the "Profile" section from the top-right user menu</li>
              <li>Upload a profile picture</li>
              <li>Verify your contact information</li>
              <li>Set your notification preferences</li>
            </ul>
            <h3>2. Explore the Dashboard</h3>
            <p>Familiarize yourself with the dashboard which provides an overview of:</p>
            <ul>
              <li>Recent activities and pending actions</li>
              <li>Key performance metrics</li>
              <li>System status and alerts</li>
            </ul>
            <h3>3. Review Your Permissions</h3>
            <p>Understand what you can access and actions you can perform:</p>
            <ul>
              <li>Your permissions are set based on your administrator role</li>
              <li>You can view your permissions in the "Profile" section</li>
              <li>If you need additional access, contact a super administrator</li>
            </ul>
            <h3>4. Set Up Security</h3>
            <p>Enhance your account security:</p>
            <ul>
              <li>Change your temporary password</li>
              <li>Enable two-factor authentication</li>
              <li>Review the security best practices in the Security help section</li>
            </ul>
            <h3>5. Next Steps</h3>
            <p>After completing the initial setup:</p>
            <ul>
              <li>Explore the various modules you have access to</li>
              <li>Review any pending items that require your attention</li>
              <li>Check out the video tutorials for more detailed guidance</li>
            </ul>
          `,
        forSuperAdmin: true,
        forMdaAdmin: true,
        icon: <CheckCircleIcon size={20} className="text-green-600" data-id="element-1686" />
      }, {
        id: 'gs-dashboard',
        title: 'Understanding Your Dashboard',
        category: 'getting-started',
        content: `
            <h2>Understanding Your Admin Dashboard</h2>
            <p>The dashboard is your command center for monitoring and managing the platform. This guide explains each component and how to use the information effectively.</p>
            <h3>Dashboard Components</h3>
            <h4>1. Key Performance Indicators (KPIs)</h4>
            <p>The top section displays critical metrics:</p>
            <ul>
              <li><strong>Total Applications:</strong> Number of service applications received</li>
              <li><strong>Pending Applications:</strong> Applications awaiting processing</li>
              <li><strong>Total Revenue:</strong> Revenue generated through the platform</li>
              <li><strong>User Registrations:</strong> New user accounts created</li>
            </ul>
            <h4>2. Recent Activity</h4>
            <p>This section shows the most recent actions and events:</p>
            <ul>
              <li>New applications submitted</li>
              <li>Status changes to existing applications</li>
              <li>New user registrations</li>
              <li>Payment transactions</li>
            </ul>
            <p>Click on any item to view more details or take action.</p>
            <h4>3. Pending Tasks</h4>
            <p>This highlights items requiring your attention:</p>
            <ul>
              <li>Applications awaiting review</li>
              <li>Approvals needed</li>
              <li>Issues flagged for resolution</li>
            </ul>
            <h4>4. Quick Actions</h4>
            <p>Shortcuts to common tasks:</p>
            <ul>
              <li>Process new applications</li>
              <li>Generate reports</li>
              <li>Add new services</li>
              <li>Manage users</li>
            </ul>
            <h4>5. Performance Charts</h4>
            <p>Visual representations of key metrics:</p>
            <ul>
              <li>Application processing times</li>
              <li>Revenue trends</li>
              <li>User activity patterns</li>
              <li>Service popularity</li>
            </ul>
            <h3>Customizing Your Dashboard</h3>
            <p>You can personalize your dashboard view:</p>
            <ul>
              <li>Click the settings icon in each widget to configure or hide it</li>
              <li>Drag and drop widgets to rearrange them</li>
              <li>Use the "Add Widget" button to add more information sections</li>
            </ul>
            <h3>Refreshing Data</h3>
            <p>Dashboard data refreshes automatically every few minutes. To manually refresh:</p>
            <ul>
              <li>Click the refresh icon in the top right corner</li>
              <li>Or refresh individual widgets using their respective refresh buttons</li>
            </ul>
          `,
        forSuperAdmin: true,
        forMdaAdmin: true,
        icon: <LayoutIcon size={20} className="text-indigo-600" data-id="element-1687" />,
        videoUrl: 'https://www.example.com/videos/admin-dashboard-guide'
      },
      // User Management Topics
      {
        id: 'um-managing-users',
        title: 'Managing User Accounts',
        category: 'user-management',
        content: `
            <h2>Managing User Accounts</h2>
            <p>This guide covers how to effectively manage user accounts on the platform, including creating, editing, and deactivating accounts.</p>
            <h3>Viewing User Accounts</h3>
            <p>To access the user management section:</p>
            <ol>
              <li>Navigate to "User Management" in the main menu</li>
              <li>The default view shows all active users</li>
              <li>Use the filters to sort and find specific users by name, email, registration date, etc.</li>
            </ol>
            <h3>Creating New Admin Users</h3>
            ${isSuperAdmin() ? `<p>As a Super Administrator, you can create new admin accounts:</p>
              <ol>
                <li>Click the "Add User" button in the User Management section</li>
                <li>Select "Admin User" as the account type</li>
                <li>Fill in the required information (name, email, phone number)</li>
                <li>Assign the appropriate role (Super Admin or MDA Admin)</li>
                <li>If creating an MDA Admin, select the associated MDA</li>
                <li>Set the appropriate permissions</li>
                <li>Click "Create Account" to generate the account</li>
              </ol>
              <p>The new admin will receive an email with instructions to set their password.</p>` : `<p>As an MDA Administrator, you can create admin accounts for your MDA:</p>
              <ol>
                <li>Click the "Add User" button in the User Management section</li>
                <li>Fill in the required information (name, email, phone number)</li>
                <li>Assign the appropriate permissions within your MDA scope</li>
                <li>Click "Create Account" to generate the account</li>
              </ol>
              <p>The new admin will receive an email with instructions to set their password.</p>`}
            <h3>Editing User Information</h3>
            <p>To update a user's information:</p>
            <ol>
              <li>Find the user in the user list</li>
              <li>Click the "Edit" button (pencil icon)</li>
              <li>Update the necessary fields</li>
              <li>Click "Save Changes"</li>
            </ol>
            <h3>Resetting User Passwords</h3>
            <p>If a user needs their password reset:</p>
            <ol>
              <li>Find the user in the user list</li>
              <li>Click the "Actions" dropdown</li>
              <li>Select "Reset Password"</li>
              <li>Confirm the action</li>
            </ol>
            <p>The user will receive an email with instructions to set a new password.</p>
            <h3>Deactivating User Accounts</h3>
            <p>To temporarily disable a user account:</p>
            <ol>
              <li>Find the user in the user list</li>
              <li>Click the "Actions" dropdown</li>
              <li>Select "Deactivate Account"</li>
              <li>Enter a reason for deactivation</li>
              <li>Click "Deactivate"</li>
            </ol>
            <p>Deactivated accounts can be reactivated later if needed.</p>
            <h3>Deleting User Accounts</h3>
            <p>Permanently removing a user account should be done with caution:</p>
            <ol>
              <li>Find the user in the user list</li>
              <li>Click the "Actions" dropdown</li>
              <li>Select "Delete Account"</li>
              <li>Review the warning about data loss</li>
              <li>Enter the confirmation text</li>
              <li>Click "Delete Permanently"</li>
            </ol>
            <p><strong>Note:</strong> This action cannot be undone and will remove all user data from the system.</p>
            <h3>User Activity Logs</h3>
            <p>To view a user's activity history:</p>
            <ol>
              <li>Find the user in the user list</li>
              <li>Click on their name to open their profile</li>
              <li>Navigate to the "Activity Log" tab</li>
            </ol>
            <p>This shows login history, actions performed, and other relevant activities.</p>
          `,
        forSuperAdmin: true,
        forMdaAdmin: true,
        icon: <UserIcon size={20} className="text-blue-600" data-id="element-1688" />,
        videoUrl: 'https://www.example.com/videos/managing-user-accounts'
      }, {
        id: 'um-roles-permissions',
        title: 'Understanding Roles and Permissions',
        category: 'user-management',
        content: `
            <h2>Understanding Roles and Permissions</h2>
            <p>This guide explains the role-based access control system and how to assign appropriate permissions to different user types.</p>
            <h3>Role Hierarchy</h3>
            <p>The platform uses a hierarchical role system:</p>
            <ul>
              <li><strong>Super Administrators:</strong> Have complete access to all platform features and can manage all MDAs</li>
              <li><strong>MDA Administrators:</strong> Have administrative access limited to their specific MDA</li>
              <li><strong>MDA Staff:</strong> Have operational access to specific functions within their MDA</li>
              <li><strong>Citizens:</strong> Regular users who access services through the public portal</li>
            </ul>
            ${isSuperAdmin() ? `<h3>Super Administrator Capabilities</h3>
              <p>As a Super Administrator, you can:</p>
              <ul>
                <li>Create and manage all types of user accounts</li>
                <li>Create and configure MDAs</li>
                <li>Manage platform-wide settings</li>
                <li>Access all reports and analytics</li>
                <li>Override approvals when necessary</li>
                <li>Manage payment configurations</li>
                <li>Configure system-wide notifications</li>
              </ul>` : `<h3>MDA Administrator Capabilities</h3>
              <p>As an MDA Administrator for ${getMdaName()}, you can:</p>
              <ul>
                <li>Create and manage staff accounts within your MDA</li>
                <li>Manage services offered by your MDA</li>
                <li>Process applications submitted to your MDA</li>
                <li>View reports related to your MDA</li>
                <li>Configure MDA-specific settings</li>
                <li>Manage MDA-specific notifications</li>
              </ul>`}
            <h3>Permission Types</h3>
            <p>Permissions are grouped into categories:</p>
            <ul>
              <li><strong>Read:</strong> Allows viewing information</li>
              <li><strong>Write:</strong> Allows creating and editing information</li>
              <li><strong>Approve:</strong> Allows approving or rejecting requests</li>
              <li><strong>Delete:</strong> Allows removing information</li>
              <li><strong>Export:</strong> Allows downloading or exporting data</li>
            </ul>
            <h3>Assigning Permissions</h3>
            ${isSuperAdmin() ? `<p>To assign permissions to a user:</p>
              <ol>
                <li>Navigate to "User Management"</li>
                <li>Find and select the user</li>
                <li>Click the "Edit Permissions" button</li>
                <li>Check or uncheck permissions as needed</li>
                <li>Click "Save Changes"</li>
              </ol>` : `<p>To assign permissions to a staff member in your MDA:</p>
              <ol>
                <li>Navigate to "User Management"</li>
                <li>Find and select the staff member</li>
                <li>Click the "Edit Permissions" button</li>
                <li>Check or uncheck permissions within your MDA scope</li>
                <li>Click "Save Changes"</li>
              </ol>`}
            <h3>Permission Best Practices</h3>
            <p>Follow these guidelines when assigning permissions:</p>
            <ul>
              <li>Follow the principle of least privilege - give users only the permissions they need</li>
              <li>Regularly review and audit user permissions</li>
              <li>Create role templates for common job functions to ensure consistency</li>
              <li>Document the reasoning behind special permission assignments</li>
              <li>Remove unnecessary permissions when a user's role changes</li>
            </ul>
            <h3>Permission Conflicts</h3>
            <p>When permissions conflict:</p>
            <ul>
              <li>Explicit denials override allowances</li>
              <li>More specific permissions override general ones</li>
              <li>Direct permissions override inherited ones</li>
            </ul>
            <p>If you encounter unexpected permission behavior, check for conflicts in the permission hierarchy.</p>
          `,
        forSuperAdmin: true,
        forMdaAdmin: true,
        icon: <LockIcon size={20} className="text-red-600" data-id="element-1689" />
      },
      // MDA Management Topics (Super Admin only)
      {
        id: 'mda-adding-mdas',
        title: 'Adding and Configuring MDAs',
        category: 'mda-management',
        content: `
            <h2>Adding and Configuring MDAs</h2>
            <p>This guide covers how to add new Ministries, Departments, and Agencies (MDAs) to the platform and configure their settings.</p>
            <h3>Adding a New MDA</h3>
            <p>To add a new MDA to the system:</p>
            <ol>
              <li>Navigate to "MDA Management" in the main menu</li>
              <li>Click the "Add MDA" button</li>
              <li>Fill in the required information:
                <ul>
                  <li>Full name of the MDA</li>
                  <li>Short name/acronym</li>
                  <li>Description</li>
                  <li>Contact information (address, phone, email)</li>
                  <li>Logo (upload an image file)</li>
                  <li>MDA type (Ministry, Department, Agency, or Other)</li>
                  <li>Parent organization (if applicable)</li>
                </ul>
              </li>
              <li>Click "Create MDA"</li>
            </ol>
            <h3>Configuring MDA Settings</h3>
            <p>After creating an MDA, configure its settings:</p>
            <ol>
              <li>Select the MDA from the list</li>
              <li>Navigate to the "Settings" tab</li>
              <li>Configure the following:
                <ul>
                  <li><strong>Operational Hours:</strong> Set the working hours and days</li>
                  <li><strong>Service Categories:</strong> Define the categories of services offered</li>
                  <li><strong>Application Workflow:</strong> Configure the approval process for applications</li>
                  <li><strong>Payment Settings:</strong> Set up payment options and accounts</li>
                  <li><strong>Notification Templates:</strong> Customize email and SMS templates</li>
                  <li><strong>Document Requirements:</strong> Define document types for applications</li>
                </ul>
              </li>
              <li>Save each section as you complete it</li>
            </ol>
            <h3>Assigning MDA Administrators</h3>
            <p>To assign administrators to an MDA:</p>
            <ol>
              <li>Navigate to the "Administrators" tab of the MDA</li>
              <li>Click "Add Administrator"</li>
              <li>You can either:
                <ul>
                  <li>Create a new admin account by providing name, email, and contact details</li>
                  <li>Assign an existing user as an admin by searching for their account</li>
                </ul>
              </li>
              <li>Set the appropriate permissions for the administrator</li>
              <li>Click "Assign Administrator"</li>
            </ol>
            <p>New administrators will receive an email notification about their assignment.</p>
            <h3>Managing MDA Services</h3>
            <p>To add services to an MDA:</p>
            <ol>
              <li>Navigate to the "Services" tab of the MDA</li>
              <li>Click "Add Service"</li>
              <li>Fill in the service details:
                <ul>
                  <li>Service name and description</li>
                  <li>Category</li>
                  <li>Required documents</li>
                  <li>Processing time</li>
                  <li>Fees</li>
                  <li>Application form fields</li>
                </ul>
              </li>
              <li>Configure the approval workflow</li>
              <li>Set the service status (Draft, Active, Inactive)</li>
              <li>Click "Create Service"</li>
            </ol>
            <h3>Deactivating an MDA</h3>
            <p>If an MDA needs to be temporarily disabled:</p>
            <ol>
              <li>Find the MDA in the MDA list</li>
              <li>Click the "Actions" dropdown</li>
              <li>Select "Deactivate MDA"</li>
              <li>Enter a reason for deactivation</li>
              <li>Click "Deactivate"</li>
            </ol>
            <p>Deactivated MDAs will not be visible to citizens, but their data is preserved.</p>
            <h3>MDA Performance Monitoring</h3>
            <p>To monitor an MDA's performance:</p>
            <ol>
              <li>Select the MDA from the list</li>
              <li>Navigate to the "Analytics" tab</li>
              <li>View key metrics:
                <ul>
                  <li>Application volume and processing times</li>
                  <li>Revenue generated</li>
                  <li>User satisfaction ratings</li>
                  <li>Service popularity</li>
                </ul>
              </li>
              <li>Use the date filters to analyze trends over time</li>
              <li>Export reports as needed for further analysis</li>
            </ol>
          `,
        forSuperAdmin: true,
        forMdaAdmin: false,
        icon: <BuildingIcon size={20} className="text-purple-600" data-id="element-1690" />,
        videoUrl: 'https://www.example.com/videos/adding-configuring-mdas'
      },
      // Applications Management Topics
      {
        id: 'app-processing',
        title: 'Processing Service Applications',
        category: 'applications',
        content: `
            <h2>Processing Service Applications</h2>
            <p>This guide explains how to efficiently process service applications submitted by citizens.</p>
            <h3>Accessing Applications</h3>
            <p>To access applications waiting for processing:</p>
            <ol>
              <li>Navigate to "Applications" in the main menu</li>
              <li>The default view shows pending applications</li>
              <li>Use the filters to sort by date, service type, status, or search for specific applications</li>
            </ol>
            <h3>Application Statuses</h3>
            <p>Applications can have the following statuses:</p>
            <ul>
              <li><strong>New:</strong> Recently submitted, not yet reviewed</li>
              <li><strong>Under Review:</strong> Currently being processed</li>
              <li><strong>Additional Information Required:</strong> Waiting for more information from the applicant</li>
              <li><strong>Approved:</strong> Application has been approved</li>
              <li><strong>Rejected:</strong> Application has been denied</li>
              <li><strong>Completed:</strong> Service has been delivered</li>
              <li><strong>Cancelled:</strong> Withdrawn by the applicant</li>
            </ul>
            <h3>Reviewing an Application</h3>
            <p>To review an application:</p>
            <ol>
              <li>Click on the application from the list to open it</li>
              <li>Review the applicant's information and submitted documents</li>
              <li>Check that all required fields are completed correctly</li>
              <li>Verify that all required documents are attached and valid</li>
              <li>Check payment status if the service requires payment</li>
              <li>Add notes to the application if needed</li>
            </ol>
            <h3>Processing Steps</h3>
            <p>After reviewing, take one of these actions:</p>
            <ol>
              <li><strong>Approve:</strong> If the application meets all requirements
                <ul>
                  <li>Click the "Approve" button</li>
                  <li>Add any approval notes</li>
                  <li>Select the next step in the workflow if applicable</li>
                  <li>Click "Confirm Approval"</li>
                </ul>
              </li>
              <li><strong>Request Additional Information:</strong> If more details are needed
                <ul>
                  <li>Click the "Request Info" button</li>
                  <li>Specify what information is needed</li>
                  <li>Click "Send Request"</li>
                </ul>
              </li>
              <li><strong>Reject:</strong> If the application cannot be approved
                <ul>
                  <li>Click the "Reject" button</li>
                  <li>Provide a detailed reason for rejection</li>
                  <li>Click "Confirm Rejection"</li>
                </ul>
              </li>
            </ol>
            <h3>Application Timeline</h3>
            <p>Each application has a timeline showing:</p>
            <ul>
              <li>When the application was submitted</li>
              <li>All actions taken on the application</li>
              <li>Who performed each action</li>
              <li>Notes and comments added</li>
              <li>Status changes</li>
            </ul>
            <p>This timeline provides a complete audit trail of the application process.</p>
            <h3>Communicating with Applicants</h3>
            <p>To send a message to an applicant:</p>
            <ol>
              <li>Open the application</li>
              <li>Click the "Message Applicant" button</li>
              <li>Type your message</li>
              <li>Click "Send Message"</li>
            </ol>
            <p>The applicant will receive an email notification and can respond through the citizen portal.</p>
            <h3>Bulk Processing</h3>
            <p>To process multiple applications at once:</p>
            <ol>
              <li>Select the applications by checking the boxes next to them</li>
              <li>Click the "Bulk Actions" dropdown</li>
              <li>Select the action to perform</li>
              <li>Confirm the action</li>
            </ol>
            <p><strong>Note:</strong> Bulk actions should be used carefully and only for applications that require the same processing decision.</p>
            <h3>Processing Deadlines</h3>
            <p>Applications are color-coded based on their processing deadline:</p>
            <ul>
              <li><strong>Green:</strong> Plenty of time remaining</li>
              <li><strong>Yellow:</strong> Approaching deadline</li>
              <li><strong>Red:</strong> Past deadline</li>
            </ul>
            <p>Prioritize applications that are approaching or past their deadlines to maintain service level agreements.</p>
          `,
        forSuperAdmin: true,
        forMdaAdmin: true,
        icon: <FileTextIcon size={20} className="text-orange-600" data-id="element-1691" />,
        videoUrl: 'https://www.example.com/videos/processing-applications'
      },
      // Services Management Topics
      {
        id: 'srv-managing',
        title: 'Managing Government Services',
        category: 'services',
        content: `
            <h2>Managing Government Services</h2>
            <p>This guide explains how to create, configure, and manage government services offered through the platform.</p>
            <h3>Accessing Service Management</h3>
            <p>To access the service management section:</p>
            <ol>
              <li>Navigate to "Services" in the main menu</li>
              <li>The default view shows all active services${isSuperAdmin() ? ' across all MDAs' : ' for your MDA'}</li>
              <li>Use the filters to sort and find specific services</li>
            </ol>
            <h3>Creating a New Service</h3>
            <p>To add a new service:</p>
            <ol>
              <li>Click the "Add Service" button</li>
              <li>Fill in the basic service information:
                <ul>
                  <li>Service name and description</li>
                  <li>Category</li>
                  <li>Responsible department${isSuperAdmin() ? ' within the MDA' : ''}</li>
                  <li>Service icon (upload an image)</li>
                </ul>
              </li>
              <li>Configure eligibility requirements</li>
              <li>Set processing time expectations</li>
              <li>Configure service fees</li>
              <li>Create the application form</li>
              <li>Define required documents</li>
              <li>Set up the approval workflow</li>
              <li>Click "Save as Draft" or "Publish Service"</li>
            </ol>
            <h3>Creating Application Forms</h3>
            <p>To create the application form for a service:</p>
            <ol>
              <li>In the service editor, navigate to the "Application Form" tab</li>
              <li>Click "Add Section" to create form sections</li>
              <li>For each section, add form fields:
                <ul>
                  <li>Text fields (short and long)</li>
                  <li>Number fields</li>
                  <li>Date fields</li>
                  <li>Dropdown selections</li>
                  <li>Checkboxes</li>
                  <li>Radio buttons</li>
                  <li>File uploads</li>
                </ul>
              </li>
              <li>Configure each field:
                <ul>
                  <li>Set as required or optional</li>
                  <li>Add validation rules</li>
                  <li>Add help text</li>
                </ul>
              </li>
              <li>Arrange fields by dragging and dropping</li>
              <li>Click "Save Form"</li>
            </ol>
            <h3>Setting Up Approval Workflows</h3>
            <p>To configure the approval process:</p>
            <ol>
              <li>In the service editor, navigate to the "Workflow" tab</li>
              <li>Define the approval steps:
                <ul>
                  <li>Add each step in the process</li>
                  <li>Assign responsible roles or departments</li>
                  <li>Set time limits for each step</li>
                  <li>Configure conditional paths if needed</li>
                </ul>
              </li>
              <li>Set up automated notifications for each step</li>
              <li>Configure what happens after final approval</li>
              <li>Click "Save Workflow"</li>
            </ol>
            <h3>Configuring Service Fees</h3>
            <p>To set up fees for a service:</p>
            <ol>
              <li>In the service editor, navigate to the "Fees" tab</li>
              <li>Click "Add Fee"</li>
              <li>Configure the fee:
                <ul>
                  <li>Fee name and description</li>
                  <li>Amount</li>
                  <li>Fee type (fixed, variable, or calculated)</li>
                  <li>For calculated fees, set up the calculation formula</li>
                  <li>Specify if the fee is required or conditional</li>
                </ul>
              </li>
              <li>Configure payment methods accepted</li>
              <li>Set up any fee waivers or discounts</li>
              <li>Click "Save Fee Configuration"</li>
            </ol>
            <h3>Publishing a Service</h3>
            <p>Once a service is fully configured:</p>
            <ol>
              <li>Review all sections for completeness and accuracy</li>
              <li>Click the "Preview" button to see how the service will appear to citizens</li>
              <li>Make any necessary adjustments</li>
              <li>Click "Publish Service" to make it available to the public</li>
            </ol>
            <p>Published services will immediately appear in the citizen portal.</p>
            <h3>Editing Existing Services</h3>
            <p>To modify an existing service:</p>
            <ol>
              <li>Find the service in the services list</li>
              <li>Click the "Edit" button</li>
              <li>Make the necessary changes</li>
              <li>Click "Save Changes"</li>
              <li>If the service is already published, you'll be asked if you want to:
                <ul>
                  <li>Update the live service immediately</li>
                  <li>Save changes as a draft version</li>
                </ul>
              </li>
            </ol>
            <h3>Deactivating a Service</h3>
            <p>To temporarily remove a service from the public portal:</p>
            <ol>
              <li>Find the service in the services list</li>
              <li>Click the "Actions" dropdown</li>
              <li>Select "Deactivate Service"</li>
              <li>Enter a reason for deactivation</li>
              <li>Specify if existing applications should continue to be processed</li>
              <li>Click "Deactivate"</li>
            </ol>
            <p>Deactivated services can be reactivated later if needed.</p>
          `,
        forSuperAdmin: true,
        forMdaAdmin: true,
        icon: <LayoutIcon size={20} className="text-indigo-600" data-id="element-1692" />,
        videoUrl: 'https://www.example.com/videos/managing-services'
      },
      // Payment Management Topics
      {
        id: 'pay-processing',
        title: 'Payment Processing and Management',
        category: 'payments',
        content: `
            <h2>Payment Processing and Management</h2>
            <p>This guide explains how to manage and process payments for government services.</p>
            <h3>Accessing Payment Management</h3>
            <p>To access the payment management section:</p>
            <ol>
              <li>Navigate to "Payments" in the main menu</li>
              <li>The default view shows recent payment transactions${isSuperAdmin() ? ' across all MDAs' : ' for your MDA'}</li>
              <li>Use the filters to sort and find specific payments</li>
            </ol>
            <h3>Understanding Payment Statuses</h3>
            <p>Payments can have the following statuses:</p>
            <ul>
              <li><strong>Pending:</strong> Payment initiated but not completed</li>
              <li><strong>Successful:</strong> Payment completed successfully</li>
              <li><strong>Failed:</strong> Payment attempt failed</li>
              <li><strong>Refunded:</strong> Payment has been refunded</li>
              <li><strong>Disputed:</strong> Payment is under dispute</li>
              <li><strong>Partially Refunded:</strong> Part of the payment has been refunded</li>
            </ul>
            <h3>Viewing Payment Details</h3>
            <p>To view details of a specific payment:</p>
            <ol>
              <li>Click on the payment in the list</li>
              <li>The payment details page shows:
                <ul>
                  <li>Transaction ID and reference numbers</li>
                  <li>Payment amount and fees</li>
                  <li>Payment method used</li>
                  <li>Date and time of payment</li>
                  <li>Associated service and application</li>
                  <li>Payment processor response</li>
                  <li>Receipt information</li>
                </ul>
              </li>
            </ol>
            <h3>Processing Manual Payments</h3>
            <p>For payments made outside the online system:</p>
            <ol>
              <li>Click "Record Manual Payment" button</li>
              <li>Enter the payment details:
                <ul>
                  <li>Select the application/service</li>
                  <li>Enter the amount paid</li>
                  <li>Select payment method (cash, bank transfer, etc.)</li>
                  <li>Enter reference numbers if applicable</li>
                  <li>Upload proof of payment if available</li>
                </ul>
              </li>
              <li>Click "Record Payment"</li>
            </ol>
            <p>The system will update the application status and notify the applicant.</p>
            <h3>Issuing Refunds</h3>
            <p>To process a refund:</p>
            <ol>
              <li>Find the payment in the list</li>
              <li>Click the "Actions" dropdown</li>
              <li>Select "Issue Refund"</li>
              <li>Choose full or partial refund</li>
              <li>For partial refunds, enter the amount to refund</li>
              <li>Enter a reason for the refund</li>
              <li>Click "Process Refund"</li>
            </ol>
            <p>The system will process the refund through the same payment method if possible, or provide instructions for manual refunds.</p>
            <h3>Handling Payment Disputes</h3>
            <p>When a payment is disputed:</p>
            <ol>
              <li>Find the payment in the list</li>
              <li>Click the "Actions" dropdown</li>
              <li>Select "Mark as Disputed"</li>
              <li>Enter details about the dispute</li>
              <li>Upload any relevant documentation</li>
              <li>Click "Save Dispute Information"</li>
            </ol>
            <p>Once a dispute is resolved, update the status accordingly.</p>
            <h3>Generating Payment Reports</h3>
            <p>To create payment reports:</p>
            <ol>
              <li>Click the "Reports" button in the Payments section</li>
              <li>Select the report type:
                <ul>
                  <li>Daily collection summary</li>
                  <li>Revenue by service</li>
                  <li>Payment method analysis</li>
                  <li>Failed payment analysis</li>
                  <li>Custom report</li>
                </ul>
              </li>
              <li>Set the date range</li>
              <li>Apply any filters needed</li>
              <li>Click "Generate Report"</li>
              <li>The report can be viewed online, downloaded, or printed</li>
            </ol>
            <h3>${isSuperAdmin() ? 'Configuring Payment Settings' : 'Understanding Payment Settings'}</h3>
            ${isSuperAdmin() ? `<p>To configure payment settings:</p>
              <ol>
                <li>Navigate to the "Settings" tab in the Payments section</li>
                <li>Configure payment processors:
                  <ul>
                    <li>Add or update payment gateway credentials</li>
                    <li>Set transaction fee handling</li>
                    <li>Configure payment method options</li>
                  </ul>
                </li>
                <li>Set up receipt templates</li>
                <li>Configure automatic reconciliation settings</li>
                <li>Set up notification preferences for payment events</li>
                <li>Click "Save Settings"</li>
              </ol>` : `<p>Payment settings are configured by Super Administrators, but as an MDA Administrator, you should understand:</p>
              <ul>
                <li>Which payment methods are available for your services</li>
                <li>How transaction fees are handled</li>
                <li>The reconciliation schedule for payments</li>
                <li>How to interpret payment reports for your MDA</li>
              </ul>`}
            <h3>Reconciling Payments</h3>
            <p>To reconcile payments with bank statements:</p>
            <ol>
              <li>Click the "Reconciliation" button in the Payments section</li>
              <li>Select the date range for reconciliation</li>
              <li>Upload bank statements if required</li>
              <li>The system will match transactions automatically</li>
              <li>Review unmatched transactions</li>
              <li>Manually match or flag discrepancies</li>
              <li>Generate a reconciliation report</li>
              <li>Mark the period as reconciled</li>
            </ol>
          `,
        forSuperAdmin: true,
        forMdaAdmin: true,
        icon: <CreditCardIcon size={20} className="text-green-600" data-id="element-1693" />
      },
      // Reports & Analytics Topics
      {
        id: 'rep-generating',
        title: 'Generating and Analyzing Reports',
        category: 'reports',
        content: `
            <h2>Generating and Analyzing Reports</h2>
            <p>This guide explains how to create, customize, and interpret reports and analytics in the admin portal.</p>
            <h3>Accessing Reports</h3>
            <p>To access the reports and analytics section:</p>
            <ol>
              <li>Navigate to "Reports & Analytics" in the main menu</li>
              <li>The dashboard shows key metrics and predefined reports${isSuperAdmin() ? ' across all MDAs' : ' for your MDA'}</li>
              <li>Use the filters to customize the view</li>
            </ol>
            <h3>Types of Reports Available</h3>
            <p>The system offers several report categories:</p>
            <ul>
              <li><strong>Operational Reports:</strong>
                <ul>
                  <li>Application volume and processing times</li>
                  <li>Approval/rejection rates</li>
                  <li>Staff performance metrics</li>
                </ul>
              </li>
              <li><strong>Financial Reports:</strong>
                <ul>
                  <li>Revenue collection</li>
                  <li>Payment method distribution</li>
                  <li>Service fee analysis</li>
                </ul>
              </li>
              <li><strong>User Reports:</strong>
                <ul>
                  <li>User registration trends</li>
                  <li>Demographic information</li>
                  <li>User activity patterns</li>
                </ul>
              </li>
              <li><strong>Service Reports:</strong>
                <ul>
                  <li>Service popularity</li>
                  <li>Service performance</li>
                  <li>Feedback and ratings</li>
                </ul>
              </li>
              ${isSuperAdmin() ? `<li><strong>MDA Comparison Reports:</strong>
                  <ul>
                    <li>Performance benchmarking</li>
                    <li>Revenue comparison</li>
                    <li>Efficiency metrics</li>
                  </ul>
                </li>` : ''}
            </ul>
            <h3>Creating a Custom Report</h3>
            <p>To generate a custom report:</p>
            <ol>
              <li>Click the "Create Report" button</li>
              <li>Select the report type</li>
              <li>Choose the metrics to include</li>
              <li>Set the date range</li>
              <li>Apply filters as needed</li>
              <li>Select the visualization type (table, chart, etc.)</li>
              <li>Click "Generate Report"</li>
              <li>Once generated, you can view, download, or share the report</li>
            </ol>
            <h3>Interpreting Dashboard Metrics</h3>
            <p>The dashboard provides at-a-glance metrics:</p>
            <ul>
              <li><strong>Total Revenue:</strong> Total fees collected</li>
              <li><strong>Applications:</strong> Number of applications received</li>
              <li><strong>Approval Rate:</strong> Percentage of approved applications</li>
              <li><strong>Processing Time:</strong> Average time to process applications</li>
              <li><strong>User Satisfaction:</strong> Rating based on feedback</li>
            </ul>
            <p>Each metric can be clicked to view more detailed information.</p>
            <h3>Exporting Reports</h3>
            <p>To export a report:</p>
            <ol>
              <li>Generate the desired report</li>
              <li>Click the "Export" button</li>
              <li>Select the export format:
                <ul>
                  <li>PDF</li>
                  <li>Excel</li>
                  <li>CSV</li>
                </ul>
              </li>
              <li>Choose export options (include charts, raw data, etc.)</li>
              <li>Click "Download"</li>
            </ol>
            <h3>Scheduling Regular Reports</h3>
            <p>To schedule automated reports:</p>
            <ol>
              <li>Create and save a report template</li>
              <li>Click "Schedule Report"</li>
              <li>Set the frequency (daily, weekly, monthly)</li>
              <li>Choose delivery method (email, dashboard, or both)</li>
              <li>Add recipients if sending by email</li>
              <li>Click "Save Schedule"</li>
            </ol>
            <p>Scheduled reports will be generated automatically and delivered as specified.</p>
            <h3>Data Visualization Options</h3>
            <p>The system offers various visualization types:</p>
            <ul>
              <li><strong>Bar Charts:</strong> Good for comparing categories</li>
              <li><strong>Line Charts:</strong> Ideal for showing trends over time</li>
              <li><strong>Pie Charts:</strong> Best for showing proportions</li>
              <li><strong>Tables:</strong> For detailed data review</li>
              <li><strong>Heat Maps:</strong> For geographical distribution</li>
              <li><strong>Scatter Plots:</strong> For correlation analysis</li>
            </ul>
            <p>Choose the visualization that best represents your data and makes insights clear.</p>
            <h3>Analyzing Trends</h3>
            <p>To analyze trends over time:</p>
            <ol>
              <li>Generate a report with time-based data</li>
              <li>Select "Trend Analysis" view</li>
              <li>Choose the time interval (daily, weekly, monthly)</li>
              <li>The system will highlight:
                <ul>
                  <li>Growth or decline patterns</li>
                  <li>Seasonal variations</li>
                  <li>Anomalies in the data</li>
                  <li>Forecasted future trends</li>
                </ul>
              </li>
            </ol>
            <h3>Sharing Reports</h3>
            <p>To share a report with others:</p>
            <ol>
              <li>Generate the report</li>
              <li>Click the "Share" button</li>
              <li>Choose sharing options:
                <ul>
                  <li>Email the report directly</li>
                  <li>Generate a shareable link</li>
                  <li>Add to shared reports library</li>
                </ul>
              </li>
              <li>Set permissions for who can access the report</li>
              <li>Add a message if needed</li>
              <li>Click "Share"</li>
            </ol>
          `,
        forSuperAdmin: true,
        forMdaAdmin: true,
        icon: <BarChart2Icon size={20} className="text-blue-600" data-id="element-1694" />,
        videoUrl: 'https://www.example.com/videos/generating-reports'
      },
      // Security Topics
      {
        id: 'sec-best-practices',
        title: 'Security Best Practices',
        category: 'security',
        content: `
            <h2>Security Best Practices</h2>
            <p>This guide outlines essential security practices for administrators to protect the platform and user data.</p>
            <h3>Account Security</h3>
            <p>Protect your administrator account with these practices:</p>
            <ul>
              <li><strong>Strong Passwords:</strong>
                <ul>
                  <li>Use passwords at least 12 characters long</li>
                  <li>Include uppercase and lowercase letters, numbers, and symbols</li>
                  <li>Avoid common words or personal information</li>
                  <li>Use a different password than your other accounts</li>
                </ul>
              </li>
              <li><strong>Two-Factor Authentication (2FA):</strong>
                <ul>
                  <li>Always enable 2FA for your administrator account</li>
                  <li>Use an authenticator app rather than SMS when possible</li>
                  <li>Keep backup codes in a secure location</li>
                </ul>
              </li>
              <li><strong>Regular Password Changes:</strong>
                <ul>
                  <li>Change your password every 90 days</li>
                  <li>Change immediately if you suspect any security issue</li>
                </ul>
              </li>
            </ul>
            <h3>Secure Login Practices</h3>
            <p>Follow these guidelines when logging in:</p>
            <ul>
              <li>Only log in from secure devices and networks</li>
              <li>Avoid public computers and unsecured Wi-Fi</li>
              <li>Always check the URL to ensure you're on the correct site</li>
              <li>Look for HTTPS and the padlock icon in your browser</li>
              <li>Never share your login credentials with anyone</li>
              <li>Log out when you finish your session</li>
              <li>Close your browser after logging out</li>
            </ul>
            <h3>Handling Sensitive Information</h3>
            <p>When working with sensitive data:</p>
            <ul>
              <li>Only access the information you need to perform your duties</li>
              <li>Never download sensitive data to local devices unless absolutely necessary</li>
              <li>If you must download data, ensure it's encrypted and deleted when no longer needed</li>
              <li>Never share sensitive information via email or messaging apps</li>
              <li>Use the secure document sharing features within the platform</li>
              <li>Be aware of your surroundings when viewing sensitive information</li>
            </ul>
            <h3>Recognizing Phishing Attempts</h3>
            <p>Watch for these warning signs of phishing:</p>
            <ul>
              <li>Emails requesting urgent action regarding your account</li>
              <li>Messages with poor grammar or spelling</li>
              <li>Emails from addresses that look similar to official ones but are slightly different</li>
              <li>Requests to provide login credentials via email</li>
              <li>Unexpected attachments or links</li>
              <li>Offers that seem too good to be true</li>
            </ul>
            <p>If you receive a suspicious message:</p>
            <ul>
              <li>Don't click any links or download attachments</li>
              <li>Don't respond to the message</li>
              <li>Report it to your IT security team</li>
              <li>Delete the message</li>
            </ul>
            <h3>Device Security</h3>
            <p>Secure the devices you use to access the admin portal:</p>
            <ul>
              <li>Keep your operating system and browsers updated</li>
              <li>Use antivirus/anti-malware software and keep it updated</li>
              <li>Enable device encryption</li>
              <li>Use a screen lock and set auto-lock after short periods of inactivity</li>
              <li>Never leave your device unattended while logged in</li>
              <li>Be cautious about installing software or browser extensions</li>
            </ul>
            <h3>Reporting Security Incidents</h3>
            <p>If you suspect a security breach:</p>
            <ol>
              <li>Change your password immediately</li>
              <li>Contact the system administrator or security team</li>
              <li>Document what happened and when you noticed it</li>
              <li>Follow the security incident response procedure</li>
              <li>Be available to provide additional information as needed</li>
            </ol>
            <h3>Regular Security Practices</h3>
            <p>Make these practices part of your routine:</p>
            <ul>
              <li>Review your account activity regularly for any unauthorized access</li>
              <li>Check the "Last Login" information when you sign in</li>
              <li>Keep your contact information updated for security notifications</li>
              <li>Participate in security awareness training</li>
              <li>Stay informed about new security threats</li>
            </ul>
            <h3>Data Privacy Compliance</h3>
            <p>Ensure you follow data privacy regulations:</p>
            <ul>
              <li>Only collect and process data necessary for official purposes</li>
              <li>Understand what constitutes personally identifiable information (PII)</li>
              <li>Know the retention periods for different types of data</li>
              <li>Follow proper procedures for data deletion when required</li>
              <li>Report any data breaches immediately</li>
            </ul>
          `,
        forSuperAdmin: true,
        forMdaAdmin: true,
        icon: <LockIcon size={20} className="text-red-600" data-id="element-1695" />
      }];
      // Define FAQs
      const faqsList: FAQ[] = [{
        id: 'faq-1',
        question: 'How do I reset my administrator password?',
        answer: `
            <p>To reset your administrator password:</p>
            <ol>
              <li>Click "Forgot Password" on the admin login page</li>
              <li>Enter your email address</li>
              <li>Check your email for a password reset link</li>
              <li>Click the link and follow instructions to create a new password</li>
              <li>Log in with your new password</li>
            </ol>
            <p>If you don't receive the email, check your spam folder or contact the system administrator.</p>
          `,
        forSuperAdmin: true,
        forMdaAdmin: true
      }, {
        id: 'faq-2',
        question: 'How do I enable two-factor authentication?',
        answer: `
            <p>To enable two-factor authentication:</p>
            <ol>
              <li>Go to your profile by clicking your name in the top-right corner</li>
              <li>Select "Security Settings"</li>
              <li>Find the "Two-Factor Authentication" section</li>
              <li>Click "Enable 2FA"</li>
              <li>Choose your preferred method (authenticator app recommended)</li>
              <li>Follow the on-screen instructions to complete setup</li>
              <li>Save your backup codes in a secure location</li>
            </ol>
            <p>Once enabled, you'll need to provide a verification code each time you log in.</p>
          `,
        forSuperAdmin: true,
        forMdaAdmin: true
      }, {
        id: 'faq-3',
        question: 'How do I process a refund for a service payment?',
        answer: `
            <p>To process a refund:</p>
            <ol>
              <li>Navigate to the "Payments" section</li>
              <li>Find the payment transaction using the search or filters</li>
              <li>Click on the transaction to view details</li>
              <li>Click the "Actions" dropdown and select "Issue Refund"</li>
              <li>Choose between a full or partial refund</li>
              <li>If partial, enter the amount to refund</li>
              <li>Enter a reason for the refund</li>
              <li>Click "Process Refund" to complete the action</li>
            </ol>
            <p>The system will attempt to process the refund through the original payment method. For cash or bank transfer payments, you'll need to follow your organization's manual refund procedure.</p>
          `,
        forSuperAdmin: true,
        forMdaAdmin: true
      }, {
        id: 'faq-4',
        question: 'How do I add a new administrator account?',
        answer: `
            ${isSuperAdmin() ? `<p>To add a new administrator account:</p>
              <ol>
                <li>Navigate to "User Management"</li>
                <li>Click the "Add User" button</li>
                <li>Select "Admin User" as the account type</li>
                <li>Fill in the required information (name, email, phone number)</li>
                <li>Assign the appropriate role (Super Admin or MDA Admin)</li>
                <li>If creating an MDA Admin, select the associated MDA</li>
                <li>Set the appropriate permissions</li>
                <li>Click "Create Account" to generate the account</li>
              </ol>
              <p>The new administrator will receive an email with instructions to set their password and complete account setup.</p>` : `<p>To add a new administrator account for your MDA:</p>
              <ol>
                <li>Navigate to "User Management"</li>
                <li>Click the "Add User" button</li>
                <li>Select "Admin User" as the account type</li>
                <li>Fill in the required information (name, email, phone number)</li>
                <li>Set the appropriate permissions within your MDA scope</li>
                <li>Click "Create Account" to generate the account</li>
              </ol>
              <p>The new administrator will receive an email with instructions to set their password and complete account setup.</p>`}
          `,
        forSuperAdmin: true,
        forMdaAdmin: true
      }, {
        id: 'faq-5',
        question: 'How do I generate a custom report?',
        answer: `
            <p>To generate a custom report:</p>
            <ol>
              <li>Navigate to "Reports & Analytics"</li>
              <li>Click the "Create Report" button</li>
              <li>Select the report type (operational, financial, user, service)</li>
              <li>Choose the metrics you want to include</li>
              <li>Set the date range for the report</li>
              <li>Apply any filters needed (services, status, etc.)</li>
              <li>Select the visualization type (table, chart, etc.)</li>
              <li>Click "Generate Report"</li>
              <li>Once generated, you can view, download, or share the report</li>
            </ol>
            <p>You can also save report configurations for future use or schedule regular report generation.</p>
          `,
        forSuperAdmin: true,
        forMdaAdmin: true
      }, {
        id: 'faq-6',
        question: 'What should I do if I notice a security issue?',
        answer: `
            <p>If you notice a security issue:</p>
            <ol>
              <li>Don't panic, but act quickly</li>
              <li>Document what you've observed (screenshots, error messages, etc.)</li>
              <li>Change your password immediately if you suspect your account is compromised</li>
              <li>Report the issue to your security team or system administrator</li>
              <li>If it involves sensitive data, follow your organization's data breach protocol</li>
              <li>Do not discuss the issue on public channels or with unauthorized personnel</li>
              <li>Follow any additional instructions provided by the security team</li>
            </ol>
            <p>Prompt reporting of security issues helps minimize potential damage and protect user data.</p>
          `,
        forSuperAdmin: true,
        forMdaAdmin: true
      }, {
        id: 'faq-7',
        question: 'How do I create a new service?',
        answer: `
            <p>To create a new service:</p>
            <ol>
              <li>Navigate to the "Services" section</li>
              <li>Click the "Add Service" button</li>
              <li>Fill in the basic service information (name, description, category)</li>
              <li>Configure eligibility requirements</li>
              <li>Set processing time expectations</li>
              <li>Configure service fees</li>
              <li>Create the application form with necessary fields</li>
              <li>Define required documents</li>
              <li>Set up the approval workflow</li>
              <li>Preview the service to check how it will appear to users</li>
              <li>Click "Save as Draft" or "Publish Service"</li>
            </ol>
            <p>Published services will immediately appear in the citizen portal, while drafts can be edited further before publishing.</p>
          `,
        forSuperAdmin: true,
        forMdaAdmin: true
      }, {
        id: 'faq-8',
        question: 'How do I view and respond to user feedback?',
        answer: `
            <p>To view and respond to user feedback:</p>
            <ol>
              <li>Navigate to the "Feedback" section (under "Support" in the main menu)</li>
              <li>Browse the list of feedback submissions</li>
              <li>Click on any feedback item to view details</li>
              <li>To respond to feedback:
                <ul>
                  <li>Click the "Respond" button</li>
                  <li>Type your response</li>
                  <li>Click "Send Response"</li>
                </ul>
              </li>
              <li>You can also categorize feedback and mark items as resolved</li>
              <li>Use the "Feedback Analytics" tab to view trends and common issues</li>
            </ol>
            <p>Responding promptly to feedback helps improve user satisfaction and identify areas for improvement.</p>
          `,
        forSuperAdmin: true,
        forMdaAdmin: true
      }, {
        id: 'faq-9',
        question: 'How do I add a new MDA to the system?',
        answer: `
            <p>To add a new MDA to the system:</p>
            <ol>
              <li>Navigate to "MDA Management" in the main menu</li>
              <li>Click the "Add MDA" button</li>
              <li>Fill in the required information:
                <ul>
                  <li>Full name of the MDA</li>
                  <li>Short name/acronym</li>
                  <li>Description</li>
                  <li>Contact information (address, phone, email)</li>
                  <li>Logo (upload an image file)</li>
                  <li>MDA type (Ministry, Department, Agency, or Other)</li>
                  <li>Parent organization (if applicable)</li>
                </ul>
              </li>
              <li>Configure MDA settings:
                <ul>
                  <li>Operational hours</li>
                  <li>Service categories</li>
                  <li>Payment settings</li>
                  <li>Notification templates</li>
                </ul>
              </li>
              <li>Assign MDA administrators</li>
              <li>Click "Create MDA" to finalize</li>
            </ol>
            <p>After creating the MDA, you can add services and configure additional settings as needed.</p>
          `,
        forSuperAdmin: true,
        forMdaAdmin: false
      }, {
        id: 'faq-10',
        question: 'How do I export data from the system?',
        answer: `
            <p>To export data from the system:</p>
            <ol>
              <li>Navigate to the relevant section (Users, Applications, Payments, etc.)</li>
              <li>Use filters to narrow down the data if needed</li>
              <li>Click the "Export" button (usually in the top-right corner)</li>
              <li>Select the export format:
                <ul>
                  <li>CSV (for spreadsheet applications)</li>
                  <li>Excel (for Microsoft Excel)</li>
                  <li>PDF (for viewing and printing)</li>
                </ul>
              </li>
              <li>Choose what data columns to include</li>
              <li>Click "Export" to generate and download the file</li>
            </ol>
            <p><strong>Note:</strong> When exporting sensitive data, ensure you follow data protection guidelines and only export what is necessary for your work.</p>
          `,
        forSuperAdmin: true,
        forMdaAdmin: true
      }];
      // Filter topics and FAQs based on admin role
      const filteredCategories = categories.filter(category => isSuperAdmin() && category.forSuperAdmin || !isSuperAdmin() && category.forMdaAdmin);
      const filteredHelpTopics = topics.filter(topic => isSuperAdmin() && topic.forSuperAdmin || !isSuperAdmin() && topic.forMdaAdmin);
      const filteredFaqs = faqsList.filter(faq => isSuperAdmin() && faq.forSuperAdmin || !isSuperAdmin() && faq.forMdaAdmin);
      setHelpCategories(filteredCategories);
      setHelpTopics(filteredHelpTopics);
      setFilteredTopics(filteredHelpTopics);
      setFaqs(filteredFaqs);
      setActiveCategory(filteredCategories[0].id);
      setIsLoading(false);
    }, 1000);
  }, [isSuperAdmin, getMdaName]);
  // Filter topics based on search query and active category
  useEffect(() => {
    if (searchQuery.trim() === '' && !activeCategory) {
      setFilteredTopics(helpTopics);
      return;
    }
    const lowercaseQuery = searchString.toLowerCase().trim();
    const filtered = helpTopics.filter(topic => {
      const matchesSearch = lowercaseQuery === '' || topic.title.toLowerCase().includes(lowercaseQuery) || topic.content.toLowerCase().includes(lowercaseQuery);
      const matchesCategory = !activeCategory || topic.category === activeCategory;
      return matchesSearch && matchesCategory;
    });
    setFilteredTopics(filtered);
  }, [searchQuery, activeCategory, helpTopics]);
  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    if (e.target.value.trim() !== '') {
      setActiveCategory(null);
    }
  };
  // Handle category selection
  const handleCategorySelect = (categoryId: string) => {
    setActiveCategory(categoryId);
    setSearchQuery('');
  };
  // Handle topic selection
  const handleTopicSelect = (topicId: string) => {
    setActiveTopicId(topicId);
  };
  // Handle FAQ toggle
  const handleFaqToggle = (faqId: string) => {
    setActiveFaqId(activeFaqId === faqId ? null : faqId);
  };
  // Open video modal
  const openVideoModal = (videoUrl: string) => {
    setCurrentVideo(videoUrl);
    setVideoModalOpen(true);
  };
  // Submit feedback
  const handleFeedbackSubmit = () => {
    if (!feedbackMessage || !feedbackType) return;
    // Simulate API call to submit feedback
    setTimeout(() => {
      setFeedbackSubmitted(true);
      setFeedbackMessage('');
      setFeedbackType(null);
      // Reset after 3 seconds
      setTimeout(() => {
        setShowFeedbackForm(false);
        setFeedbackSubmitted(false);
      }, 3000);
    }, 1000);
  };
  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-100" data-id="element-1696">
        <div className="flex flex-col items-center" data-id="element-1697">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4" data-id="element-1698"></div>
          <p className="text-gray-600" data-id="element-1699">Loading help resources...</p>
        </div>
      </div>;
  }
  return <div className="flex h-screen bg-gray-100" data-id="element-1700">
      {/* Sidebar - hidden on mobile unless toggled */}
      <div className={`${isSidebarOpen ? 'block' : 'hidden'} lg:block fixed inset-y-0 left-0 z-50 lg:relative lg:z-0`} data-id="element-1701">
        <AdminSidebar data-id="element-1702" />
      </div>
      {/* Backdrop for mobile sidebar */}
      {isSidebarOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" onClick={() => setIsSidebarOpen(false)} data-id="element-1703"></div>}
      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden" data-id="element-1704">
        <AdminHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} data-id="element-1705" />
        <main className="flex-1 overflow-y-auto p-4 lg:p-6" data-id="element-1706">
          {/* Page Header */}
          <div className="mb-6" data-id="element-1707">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-1708">Help Center</h1>
            <p className="text-gray-600 mt-1" data-id="element-1709">
              Find guides, tutorials, and answers to common questions
            </p>
          </div>
          {/* Search Bar */}
          <div className="mb-6" data-id="element-1710">
            <div className="relative" data-id="element-1711">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1712">
                <SearchIcon size={20} className="text-gray-400" data-id="element-1713" />
              </div>
              <input type="text" className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500" placeholder="Search for help topics, guides, or FAQs..." value={searchQuery} onChange={handleSearchChange} data-id="element-1714" />
            </div>
          </div>
          {/* Quick Access Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8" data-id="element-1715">
            <div className="bg-white rounded-lg shadow p-4 hover:shadow-md transition-shadow" data-id="element-1716">
              <div className="flex items-center mb-3" data-id="element-1717">
                <div className="p-2 bg-blue-100 rounded-full mr-3" data-id="element-1718">
                  <BookOpenIcon size={20} className="text-blue-600" data-id="element-1719" />
                </div>
                <h3 className="text-lg font-medium text-gray-900" data-id="element-1720">
                  Getting Started
                </h3>
              </div>
              <p className="text-sm text-gray-600 mb-3" data-id="element-1721">
                Learn the basics of the admin portal and how to use key features
              </p>
              <button onClick={() => handleCategorySelect('getting-started')} className="text-sm text-blue-600 hover:text-blue-800 flex items-center" data-id="element-1722">
                View guides
                <ChevronRightIcon size={16} className="ml-1" data-id="element-1723" />
              </button>
            </div>
            <div className="bg-white rounded-lg shadow p-4 hover:shadow-md transition-shadow" data-id="element-1724">
              <div className="flex items-center mb-3" data-id="element-1725">
                <div className="p-2 bg-green-100 rounded-full mr-3" data-id="element-1726">
                  <PlayCircleIcon size={20} className="text-green-600" data-id="element-1727" />
                </div>
                <h3 className="text-lg font-medium text-gray-900" data-id="element-1728">
                  Video Tutorials
                </h3>
              </div>
              <p className="text-sm text-gray-600 mb-3" data-id="element-1729">
                Watch step-by-step video guides for common tasks and processes
              </p>
              <button onClick={() => {
              const topicWithVideo = helpTopics.find(topic => topic.videoUrl);
              if (topicWithVideo && topicWithVideo.videoUrl) {
                openVideoModal(topicWithVideo.videoUrl!);
              }
            }} className="text-sm text-green-600 hover:text-green-800 flex items-center" data-id="element-1730">
                Watch tutorials
                <ChevronRightIcon size={16} className="ml-1" data-id="element-1731" />
              </button>
            </div>
            <div className="bg-white rounded-lg shadow p-4 hover:shadow-md transition-shadow" data-id="element-1732">
              <div className="flex items-center mb-3" data-id="element-1733">
                <div className="p-2 bg-purple-100 rounded-full mr-3" data-id="element-1734">
                  <MessageCircleIcon size={20} className="text-purple-600" data-id="element-1735" />
                </div>
                <h3 className="text-lg font-medium text-gray-900" data-id="element-1736">
                  Contact Support
                </h3>
              </div>
              <p className="text-sm text-gray-600 mb-3" data-id="element-1737">
                Get in touch with our support team for personalized assistance
              </p>
              <button onClick={() => window.location.href = '/admin/support'} className="text-sm text-purple-600 hover:text-purple-800 flex items-center" data-id="element-1738">
                Contact us
                <ChevronRightIcon size={16} className="ml-1" data-id="element-1739" />
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-1740">
            {/* Categories Sidebar */}
            <div className="lg:col-span-1" data-id="element-1741">
              <div className="bg-white rounded-lg shadow p-4" data-id="element-1742">
                <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-1743">
                  Help Categories
                </h3>
                <nav className="space-y-1" data-id="element-1744">
                  {helpCategories.map(category => <button key={category.id} className={`w-full flex items-center px-3 py-2 text-sm rounded-md ${activeCategory === category.id ? 'bg-green-50 text-green-700' : 'text-gray-700 hover:bg-gray-50'}`} onClick={() => handleCategorySelect(category.id)} data-id="element-1745">
                      <span className="mr-3" data-id="element-1746">{category.icon}</span>
                      <span data-id="element-1747">{category.name}</span>
                    </button>)}
                </nav>
                {/* Download Help Resources */}
                <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-1748">
                  <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-1749">
                    Resources
                  </h4>
                  <div className="space-y-2" data-id="element-1750">
                    <button className="w-full flex items-center text-sm px-3 py-2 bg-gray-50 text-gray-700 rounded-md hover:bg-gray-100" data-id="element-1751">
                      <DownloadIcon size={16} className="mr-2" data-id="element-1752" />
                      Download User Manual
                    </button>
                    <button className="w-full flex items-center text-sm px-3 py-2 bg-gray-50 text-gray-700 rounded-md hover:bg-gray-100" data-id="element-1753">
                      <ClipboardIcon size={16} className="mr-2" data-id="element-1754" />
                      Quick Reference Guide
                    </button>
                    <button className="w-full flex items-center text-sm px-3 py-2 bg-gray-50 text-gray-700 rounded-md hover:bg-gray-100" data-id="element-1755">
                      <FileTextIcon size={16} className="mr-2" data-id="element-1756" />
                      Glossary of Terms
                    </button>
                  </div>
                </div>
              </div>
              {/* Need More Help Box */}
              <div className="bg-green-50 rounded-lg shadow p-4 mt-4" data-id="element-1757">
                <div className="flex items-start" data-id="element-1758">
                  <div className="flex-shrink-0" data-id="element-1759">
                    <HelpCircleIcon size={24} className="text-green-600" data-id="element-1760" />
                  </div>
                  <div className="ml-3" data-id="element-1761">
                    <h3 className="text-sm font-medium text-green-800" data-id="element-1762">
                      Need more help?
                    </h3>
                    <p className="mt-1 text-sm text-green-700" data-id="element-1763">
                      Our support team is available to assist you with any
                      questions or issues.
                    </p>
                    <div className="mt-3" data-id="element-1764">
                      <a href="#" className="text-sm font-medium text-green-700 hover:text-green-600" data-id="element-1765">
                        Contact Support →
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Main Content Area */}
            <div className="lg:col-span-3 space-y-6" data-id="element-1766">
              {/* Help Topics */}
              <div className="bg-white rounded-lg shadow" data-id="element-1767">
                <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-1768">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-1769">
                    {searchQuery ? 'Search Results' : activeCategory ? helpCategories.find(c => c.id === activeCategory)?.name : 'All Topics'}
                  </h3>
                  {searchQuery && <p className="mt-1 text-sm text-gray-500" data-id="element-1770">
                      Showing results for "{searchQuery}"
                    </p>}
                </div>
                {filteredTopics.length === 0 ? <div className="p-6 text-center" data-id="element-1771">
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100" data-id="element-1772">
                      <SearchIcon size={24} className="text-gray-400" data-id="element-1773" />
                    </div>
                    <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-1774">
                      No results found
                    </h3>
                    <p className="mt-1 text-sm text-gray-500" data-id="element-1775">
                      Try adjusting your search or filter to find what you're
                      looking for.
                    </p>
                    <div className="mt-6" data-id="element-1776">
                      <button onClick={() => {
                    setSearchQuery('');
                    setActiveCategory(helpCategories[0].id);
                  }} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" data-id="element-1777">
                        Clear search
                      </button>
                    </div>
                  </div> : <div className="divide-y divide-gray-200" data-id="element-1778">
                    {filteredTopics.map(topic => <div key={topic.id} className="p-6" data-id="element-1779">
                        <div className="flex items-start" data-id="element-1780">
                          <div className="flex-shrink-0 mt-1" data-id="element-1781">{topic.icon}</div>
                          <div className="ml-4 flex-1" data-id="element-1782">
                            <h4 className="text-lg font-medium text-gray-900" data-id="element-1783">
                              {topic.title}
                            </h4>
                            {activeTopicId === topic.id ? <div className="mt-2" data-id="element-1784">
                                <div className="prose prose-green max-w-none text-gray-500 text-sm" dangerouslySetInnerHTML={{
                          __html: topic.content
                        }} data-id="element-1785" />
                                <div className="mt-4 flex flex-wrap gap-2" data-id="element-1786">
                                  {topic.videoUrl && <button onClick={() => openVideoModal(topic.videoUrl!)} className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-green-700 bg-green-100 hover:bg-green-200" data-id="element-1787">
                                      <PlayCircleIcon size={16} className="mr-1" data-id="element-1788" />
                                      Watch video
                                    </button>}
                                  <button onClick={() => setActiveTopicId(null)} className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-1789">
                                    Close
                                  </button>
                                  <button onClick={() => {
                            setShowFeedbackForm(true);
                            setActiveTopicId(null);
                          }} className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-1790">
                                    Was this helpful?
                                  </button>
                                </div>
                              </div> : <div className="mt-2 flex items-center justify-between" data-id="element-1791">
                                <p className="text-sm text-gray-500 line-clamp-2" data-id="element-1792">
                                  {topic.content.replace(/<[^>]*>?/gm, '').substring(0, 150)}
                                  ...
                                </p>
                                <button onClick={() => handleTopicSelect(topic.id)} className="ml-4 flex-shrink-0 text-sm font-medium text-green-600 hover:text-green-500" data-id="element-1793">
                                  Read more
                                </button>
                              </div>}
                          </div>
                        </div>
                      </div>)}
                  </div>}
              </div>
              {/* FAQs Section */}
              <div className="bg-white rounded-lg shadow" data-id="element-1794">
                <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-1795">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-1796">
                    Frequently Asked Questions
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-1797">
                    Quick answers to common questions
                  </p>
                </div>
                <div className="divide-y divide-gray-200" data-id="element-1798">
                  {faqs.map(faq => <div key={faq.id} className="p-6" data-id="element-1799">
                      <button className="flex w-full text-left" onClick={() => handleFaqToggle(faq.id)} data-id="element-1800">
                        <span className="flex-shrink-0 text-green-600" data-id="element-1801">
                          {activeFaqId === faq.id ? <ChevronDownIcon size={20} data-id="element-1802" /> : <ChevronRightIcon size={20} data-id="element-1803" />}
                        </span>
                        <span className="ml-2 text-gray-900 font-medium" data-id="element-1804">
                          {faq.question}
                        </span>
                      </button>
                      {activeFaqId === faq.id && <div className="mt-4 ml-7" data-id="element-1805">
                          <div className="prose prose-green max-w-none text-gray-500 text-sm" dangerouslySetInnerHTML={{
                      __html: faq.answer
                    }} data-id="element-1806" />
                          <div className="mt-4 flex items-center space-x-4" data-id="element-1807">
                            <span className="text-sm text-gray-500" data-id="element-1808">
                              Was this helpful?
                            </span>
                            <button className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700" data-id="element-1809">
                              <ThumbsUpIcon size={16} className="mr-2" data-id="element-1810" />
                              Yes, it was helpful
                            </button>
                            <button className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700" data-id="element-1811">
                              <ThumbsDownIcon size={16} className="mr-2" data-id="element-1812" />
                              No, I need more help
                            </button>
                          </div>
                        </div>}
                    </div>)}
                </div>
              </div>
              {/* Contact Support */}
              <div className="bg-white rounded-lg shadow p-6" data-id="element-1813">
                <div className="sm:flex sm:items-center sm:justify-between" data-id="element-1814">
                  <div data-id="element-1815">
                    <h3 className="text-lg font-medium text-gray-900" data-id="element-1816">
                      Still need help?
                    </h3>
                    <p className="mt-1 text-sm text-gray-500" data-id="element-1817">
                      Our support team is ready to assist you with any questions
                      or issues.
                    </p>
                  </div>
                  <div className="mt-4 sm:mt-0" data-id="element-1818">
                    <button onClick={() => window.location.href = '/admin/support'} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" data-id="element-1819">
                      Contact Support
                    </button>
                  </div>
                </div>
                <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3" data-id="element-1820">
                  <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1821">
                    <div className="flex items-center" data-id="element-1822">
                      <div className="flex-shrink-0" data-id="element-1823">
                        <PhoneIcon size={20} className="text-green-600" data-id="element-1824" />
                      </div>
                      <div className="ml-3" data-id="element-1825">
                        <h4 className="text-sm font-medium text-gray-900" data-id="element-1826">
                          Phone Support
                        </h4>
                        <p className="mt-1 text-sm text-gray-500" data-id="element-1827">
                          Mon-Fri, 8am-5pm
                        </p>
                        <a href="tel:+2348001234567" className="mt-1 block text-sm font-medium text-green-600 hover:text-green-500" data-id="element-1828">
                          +234 800 123 4567
                        </a>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1829">
                    <div className="flex items-center" data-id="element-1830">
                      <div className="flex-shrink-0" data-id="element-1831">
                        <MailIcon size={20} className="text-green-600" data-id="element-1832" />
                      </div>
                      <div className="ml-3" data-id="element-1833">
                        <h4 className="text-sm font-medium text-gray-900" data-id="element-1834">
                          Email Support
                        </h4>
                        <p className="mt-1 text-sm text-gray-500" data-id="element-1835">
                          24/7 response within 24 hours
                        </p>
                        <a href="mailto:support@govservices.gov.ng" className="mt-1 block text-sm font-medium text-green-600 hover:text-green-500" data-id="element-1836">
                          support@govservices.gov.ng
                        </a>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1837">
                    <div className="flex items-center" data-id="element-1838">
                      <div className="flex-shrink-0" data-id="element-1839">
                        <MessageCircleIcon size={20} className="text-green-600" data-id="element-1840" />
                      </div>
                      <div className="ml-3" data-id="element-1841">
                        <h4 className="text-sm font-medium text-gray-900" data-id="element-1842">
                          Live Chat
                        </h4>
                        <p className="mt-1 text-sm text-gray-500" data-id="element-1843">
                          Mon-Fri, 9am-4pm
                        </p>
                        <button className="mt-1 block text-sm font-medium text-green-600 hover:text-green-500" data-id="element-1844">
                          Start a chat
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      {/* Video Tutorial Modal */}
      {videoModalOpen && <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4" data-id="element-1845">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full" data-id="element-1846">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-1847">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-1848">
                Video Tutorial
              </h3>
              <button onClick={() => {
            setVideoModalOpen(false);
            setCurrentVideo(null);
          }} className="text-gray-400 hover:text-gray-500" data-id="element-1849">
                <XIcon size={20} data-id="element-1850" />
              </button>
            </div>
            <div className="p-6" data-id="element-1851">
              <div className="aspect-w-16 aspect-h-9 bg-gray-100 rounded-lg" data-id="element-1852">
                {/* This would be replaced with an actual video player in production */}
                <div className="flex items-center justify-center h-full" data-id="element-1853">
                  <div className="text-center" data-id="element-1854">
                    <PlayCircleIcon size={48} className="mx-auto text-gray-400" data-id="element-1855" />
                    <p className="mt-2 text-sm text-gray-500" data-id="element-1856">
                      Video player would be embedded here: {currentVideo}
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-4 flex justify-end" data-id="element-1857">
                <button onClick={() => {
              setVideoModalOpen(false);
              setCurrentVideo(null);
            }} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-1858">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>}
      {/* Feedback Form Modal */}
      {showFeedbackForm && <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4" data-id="element-1859">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full" data-id="element-1860">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-1861">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-1862">
                Provide Feedback
              </h3>
              <button onClick={() => setShowFeedbackForm(false)} className="text-gray-400 hover:text-gray-500" data-id="element-1863">
                <XIcon size={20} data-id="element-1864" />
              </button>
            </div>
            <div className="p-6" data-id="element-1865">
              {feedbackSubmitted ? <div className="text-center py-6" data-id="element-1866">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100" data-id="element-1867">
                    <CheckCircleIcon size={24} className="text-green-600" data-id="element-1868" />
                  </div>
                  <h3 className="mt-2 text-lg font-medium text-gray-900" data-id="element-1869">
                    Thank you for your feedback!
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-1870">
                    Your input helps us improve our help resources.
                  </p>
                </div> : <>
                  <p className="text-sm text-gray-500 mb-4" data-id="element-1871">
                    Your feedback helps us improve our help resources. Please
                    let us know if you found this information helpful.
                  </p>
                  <div className="mb-4" data-id="element-1872">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1873">
                      Was this information helpful?
                    </label>
                    <div className="flex space-x-4" data-id="element-1874">
                      <button onClick={() => setFeedbackType('positive')} className={`flex items-center px-4 py-2 border rounded-md ${feedbackType === 'positive' ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-300 text-gray-700'}`} data-id="element-1875">
                        <ThumbsUpIcon size={16} className="mr-2" data-id="element-1876" />
                        Yes, it was helpful
                      </button>
                      <button onClick={() => setFeedbackType('negative')} className={`flex items-center px-4 py-2 border rounded-md ${feedbackType === 'negative' ? 'border-red-500 bg-red-50 text-red-700' : 'border-gray-300 text-gray-700'}`} data-id="element-1877">
                        <ThumbsDownIcon size={16} className="mr-2" data-id="element-1878" />
                        No, I need more help
                      </button>
                    </div>
                  </div>
                  <div className="mb-4" data-id="element-1879">
                    <label htmlFor="feedback-message" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1880">
                      Additional comments (optional)
                    </label>
                    <textarea id="feedback-message" rows={4} className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm" placeholder="Please share any additional feedback or suggestions..." value={feedbackMessage} onChange={e => setFeedbackMessage(e.target.value)} data-id="element-1881"></textarea>
                  </div>
                  <div className="flex justify-end space-x-3" data-id="element-1882">
                    <button onClick={() => setShowFeedbackForm(false)} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-1883">
                      Cancel
                    </button>
                    <button onClick={handleFeedbackSubmit} disabled={!feedbackType} className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${!feedbackType ? 'bg-gray-300 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`} data-id="element-1884">
                      Submit Feedback
                    </button>
                  </div>
                </>}
            </div>
          </div>
        </div>}
    </div>;
};
export default Help;